import {
  EURC_ADDRESS_ARC,
  MAIN_USD_STABLE_ADDRESSES,
  NATIVE_TOKEN_ADDRESS_PER_CHAIN_ID,
  USDC_ADDRESSES,
} from '@/constants/addresses';
import { CHAIN_IDS } from '@/constants/chain';

import type { Price, Quote } from './0x.types';
import { ChainAddress } from './models';
import { isTokenAddressNativeAsset, isTokenAddressWrappedNativeAsset } from './multichain';

export function isWrap(
  sellTokenAddress: string,
  buyTokenAddress: string,
  chainId: number,
): boolean {
  return (
    !!(
      isTokenAddressNativeAsset(sellTokenAddress, chainId) &&
      isTokenAddressWrappedNativeAsset(buyTokenAddress, chainId)
    ) ||
    !!(
      isTokenAddressWrappedNativeAsset(sellTokenAddress, chainId) &&
      isTokenAddressNativeAsset(buyTokenAddress, chainId)
    )
  );
}

/**
 * The static default token pair for a given chain: sell biases to USDC, buy
 * biases to the chain's native token except Arc, which defaults to EURC. Sell
 * falls back `USDC -> main USD stable -> native` for chains without USDC (e.g.
 * USDT0 on Plasma). Independent of wallet balances - see ADR 0001.
 */
export function getDefaultPairForChain(chainId: number): {
  buyAddress: ChainAddress;
  sellAddress: ChainAddress;
} {
  const nativeAddress = NATIVE_TOKEN_ADDRESS_PER_CHAIN_ID[chainId];
  const sellAddress =
    USDC_ADDRESSES[chainId] ?? MAIN_USD_STABLE_ADDRESSES[chainId] ?? nativeAddress;
  const buyAddress = chainId === CHAIN_IDS.ARC ? EURC_ADDRESS_ARC : nativeAddress;

  return { buyAddress, sellAddress };
}

/**
 * The fixed default trade pair for parameter-less trade surfaces: sell USDC ->
 * buy ETH on Ethereum, regardless of the connected wallet.
 */
export function getDefaultTradeParams() {
  const chainId = CHAIN_IDS.MAINNET;
  const { buyAddress, sellAddress } = getDefaultPairForChain(chainId);

  return {
    buyAddress,
    buyChain: chainId.toString(),
    sellAddress,
    sellChain: chainId.toString(),
  };
}

/** Whether the current EVM swap needs a manual on-chain allowance before submission. */
export function getIsApprovalRequired({
  isWrap,
  isSolana,
  isSmartWallet,
  isCurrentSwapGasless,
  allowance,
  currentPrice,
}: {
  isWrap: boolean;
  isSolana: boolean;
  isSmartWallet: boolean;
  isCurrentSwapGasless: boolean;
  allowance: bigint | undefined;
  currentPrice: Price | Quote | undefined;
}) {
  if (isWrap || isSolana || currentPrice === undefined) {
    return false;
  }

  // Smart wallets handle approvals atomically for regular swaps, but for gasless
  // trades the relayer executes the swap so the token must be pre-approved on-chain.
  if (isSmartWallet && !isCurrentSwapGasless) {
    return false;
  }

  // A signature-based approval replaces the manual on-chain approval.
  // Exclude smart wallets from this check.
  if ('approval' in currentPrice && currentPrice.approval !== null && !isSmartWallet) {
    return false;
  }

  if (!currentPrice.issues.allowance?.spender) {
    return false;
  }

  return allowance !== undefined && allowance < BigInt(currentPrice.sellAmount);
}
